/**
 * Draft Service
 *
 * Prepare/start drafts on the client; make/undo picks via Firestore transactions
 * so turn order and availability are enforced atomically.
 */

import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import {
  allocateTeamsToDraftRooms,
  moveTeamBetweenRooms,
  reorderRoomTeams,
  reshuffleRoomOrders,
  getCurrentTeamId,
  getTakenContestantIds,
  teamHasPickInRoom,
  buildTeamDraftPick,
  areAllRoomsComplete,
  canUndoDraftPick,
  isDraftReadOnly,
  DRAFT_STATUS,
  DRAFT_ROOM_STATUS,
} from '../utils/draftUtils';
import { LeagueSeasonService, TeamService } from './firebaseService';
import { hasUserClaimedTeam } from '../utils/teamUtils';

function canActForTeam({
  userId,
  userProfile,
  leagueId,
  seasonId,
  team,
  isCommissioner,
}) {
  if (!userId) return false;
  if (isCommissioner) return true;
  if (team?.players?.includes(userId)) return true;
  if (hasUserClaimedTeam(userProfile?.teams, team?.id, leagueId, seasonId)) {
    return true;
  }
  return false;
}

async function notifyDraftEmail(payload) {
  try {
    const notify = httpsCallable(functions, 'notifyDraftEvent');
    await notify(payload);
  } catch (err) {
    console.warn('Draft email notification failed:', err?.message || err);
  }
}

export class DraftService {
  static claimersMapFromStatus(claimingStatus = {}) {
    const map = {};
    for (const [teamId, status] of Object.entries(claimingStatus)) {
      map[teamId] = (status.claimedBy || []).map(u => u.id);
    }
    return map;
  }

  static async setupDraft(
    leagueId,
    seasonId,
    contestantCount,
    claimersByTeamId = {},
    roomCount
  ) {
    const teams = await TeamService.getTeams(leagueId, seasonId);
    if (!teams.length) {
      throw new Error('Add teams before setting up the draft.');
    }
    if (!contestantCount || contestantCount < 1) {
      throw new Error('Season has no contestants; cannot size draft rooms.');
    }

    const draftRooms = allocateTeamsToDraftRooms(
      teams,
      contestantCount,
      claimersByTeamId,
      Math.random,
      roomCount
    );

    await LeagueSeasonService.updateLeagueSeason(leagueId, seasonId, {
      draftRooms,
      draftOrder: draftRooms.flatMap(r => r.teamOrder),
      draftStatus: DRAFT_STATUS.NOT_STARTED,
    });

    return draftRooms;
  }

  static async saveDraftRooms(leagueId, seasonId, draftRooms) {
    await LeagueSeasonService.updateLeagueSeason(leagueId, seasonId, {
      draftRooms,
      draftOrder: draftRooms.flatMap(r => r.teamOrder),
    });
  }

  static async moveTeam(leagueId, seasonId, draftRooms, teamId, toRoomId) {
    const next = moveTeamBetweenRooms(draftRooms, teamId, toRoomId);
    await this.saveDraftRooms(leagueId, seasonId, next);
    return next;
  }

  static async reorderRoom(
    leagueId,
    seasonId,
    draftRooms,
    roomId,
    newTeamOrder
  ) {
    const next = reorderRoomTeams(draftRooms, roomId, newTeamOrder);
    await this.saveDraftRooms(leagueId, seasonId, next);
    return next;
  }

  static async reshuffleOrders(leagueId, seasonId, draftRooms, roomId = null) {
    const next = reshuffleRoomOrders(draftRooms, roomId);
    await this.saveDraftRooms(leagueId, seasonId, next);
    return next;
  }

  static async startDraft(leagueId, seasonId, draftRooms) {
    if (!draftRooms?.length) {
      throw new Error('Set up draft rooms before starting.');
    }
    const emptyRoom = draftRooms.find(r => !r.teamOrder?.length);
    if (emptyRoom) {
      throw new Error(`Draft room ${emptyRoom.id} has no teams.`);
    }

    const startedRooms = draftRooms.map(r => ({
      ...r,
      currentPickIndex: 0,
      status: DRAFT_ROOM_STATUS.IN_PROGRESS,
      pickHistory: [],
    }));

    await LeagueSeasonService.updateLeagueSeason(leagueId, seasonId, {
      draftRooms: startedRooms,
      draftStatus: DRAFT_STATUS.IN_PROGRESS,
      draftStartTime: serverTimestamp(),
    });

    await notifyDraftEmail({
      type: 'draftStarted',
      leagueId,
      seasonId,
    });

    return startedRooms;
  }

  /**
   * Make a pick for the team whose turn it is in the given room.
   * Commissioner may pick for that team; cannot skip ahead.
   */
  static async makePick({
    leagueId,
    seasonId,
    roomId,
    teamId,
    contestantId,
    userId,
    userProfile,
    isCommissioner = false,
  }) {
    const seasonRef = doc(db, 'leagues', leagueId, 'seasons', seasonId);
    const teamRef = doc(
      db,
      'leagues',
      leagueId,
      'seasons',
      seasonId,
      'teams',
      teamId
    );

    await runTransaction(db, async transaction => {
      const seasonSnap = await transaction.get(seasonRef);
      const teamSnap = await transaction.get(teamRef);

      if (!seasonSnap.exists()) throw new Error('League season not found.');
      if (!teamSnap.exists()) throw new Error('Team not found.');

      const seasonData = seasonSnap.data();
      const team = { id: teamSnap.id, ...teamSnap.data() };

      if (seasonData.draftStatus === DRAFT_STATUS.FINALIZED) {
        throw new Error('Draft is finalized and cannot be changed.');
      }
      if (seasonData.draftStatus !== DRAFT_STATUS.IN_PROGRESS) {
        throw new Error('Draft is not in progress.');
      }

      if (
        !canActForTeam({
          userId,
          userProfile,
          leagueId,
          seasonId,
          team,
          isCommissioner,
        })
      ) {
        throw new Error('You are not allowed to pick for this team.');
      }

      const draftRooms = [...(seasonData.draftRooms || [])];
      const roomIndex = draftRooms.findIndex(r => r.id === roomId);
      if (roomIndex < 0) throw new Error('Draft room not found.');

      const room = { ...draftRooms[roomIndex] };
      room.teamOrder = [...(room.teamOrder || [])];
      room.pickHistory = [...(room.pickHistory || [])];

      if (room.status === DRAFT_ROOM_STATUS.COMPLETE) {
        throw new Error('This draft room is already complete.');
      }

      const currentTeamId = getCurrentTeamId(room);
      if (currentTeamId !== teamId) {
        throw new Error("It is not this team's turn to pick.");
      }

      if (teamHasPickInRoom(team, roomId)) {
        throw new Error('This team already has a pick in this room.');
      }

      const taken = getTakenContestantIds(room);
      if (taken.has(contestantId)) {
        throw new Error(
          'That contestant has already been drafted in this room.'
        );
      }

      const pickNumber = room.pickHistory.length + 1;
      const pickRecord = {
        teamId,
        contestantId,
        pickNumber,
        madeBy: userId,
        at: Timestamp.now(),
      };

      room.pickHistory.push(pickRecord);
      room.currentPickIndex = (room.currentPickIndex ?? 0) + 1;
      if (room.currentPickIndex >= room.teamOrder.length) {
        room.status = DRAFT_ROOM_STATUS.COMPLETE;
      } else {
        room.status = DRAFT_ROOM_STATUS.IN_PROGRESS;
      }

      draftRooms[roomIndex] = room;

      const teamPick = buildTeamDraftPick({
        contestantId,
        draftRoomId: roomId,
        pickNumber,
      });
      const draftPicks = [...(team.draftPicks || []), teamPick];

      const allComplete = areAllRoomsComplete(draftRooms);
      const seasonUpdate = {
        draftRooms,
      };
      if (allComplete) {
        seasonUpdate.draftStatus = DRAFT_STATUS.COMPLETE;
        seasonUpdate.draftEndTime = serverTimestamp();
      }

      transaction.update(seasonRef, seasonUpdate);
      transaction.update(teamRef, {
        draftPicks,
        updatedAt: serverTimestamp(),
      });
    });

    await notifyDraftEmail({
      type: 'pickMade',
      leagueId,
      seasonId,
      roomId,
      teamId,
      contestantId,
    });
  }

  /**
   * Undo the most recent pick in a draft room (commissioner only).
   */
  static async undoLastPick({
    leagueId,
    seasonId,
    roomId,
    isCommissioner = false,
  }) {
    if (!isCommissioner) {
      throw new Error('Only the commissioner can undo a draft pick.');
    }

    const seasonRef = doc(db, 'leagues', leagueId, 'seasons', seasonId);
    let undone = null;

    await runTransaction(db, async transaction => {
      const seasonSnap = await transaction.get(seasonRef);
      if (!seasonSnap.exists()) throw new Error('League season not found.');

      const seasonData = seasonSnap.data();
      if (seasonData.draftStatus === DRAFT_STATUS.FINALIZED) {
        throw new Error('Draft is finalized and picks cannot be undone.');
      }
      if (!canUndoDraftPick(seasonData.draftStatus)) {
        throw new Error('Draft is not active.');
      }

      const draftRooms = [...(seasonData.draftRooms || [])];
      const roomIndex = draftRooms.findIndex(r => r.id === roomId);
      if (roomIndex < 0) throw new Error('Draft room not found.');

      const room = { ...draftRooms[roomIndex] };
      room.teamOrder = [...(room.teamOrder || [])];
      room.pickHistory = [...(room.pickHistory || [])];

      if (!room.pickHistory.length) {
        throw new Error('No picks to undo in this room.');
      }

      const lastPick = room.pickHistory.pop();
      undone = lastPick;
      room.currentPickIndex = Math.max(0, (room.currentPickIndex ?? 1) - 1);
      room.status = DRAFT_ROOM_STATUS.IN_PROGRESS;
      draftRooms[roomIndex] = room;

      const teamRef = doc(
        db,
        'leagues',
        leagueId,
        'seasons',
        seasonId,
        'teams',
        lastPick.teamId
      );
      const teamSnap = await transaction.get(teamRef);
      if (!teamSnap.exists()) throw new Error('Team for last pick not found.');

      const teamData = teamSnap.data();
      const draftPicks = (teamData.draftPicks || []).filter(p => {
        const pickRoom = p.draftRoomId ?? p.weekPicked;
        const pickNum = p.pickNumber ?? p.draftId;
        return !(
          pickRoom === roomId &&
          pickNum === lastPick.pickNumber &&
          p.contestantId === lastPick.contestantId
        );
      });

      transaction.update(seasonRef, {
        draftRooms,
        draftStatus: DRAFT_STATUS.IN_PROGRESS,
        draftEndTime: null,
      });
      transaction.update(teamRef, {
        draftPicks,
        updatedAt: serverTimestamp(),
      });
    });

    await notifyDraftEmail({
      type: 'pickUndone',
      leagueId,
      seasonId,
      roomId,
      teamId: undone?.teamId,
      contestantId: undone?.contestantId,
    });

    return undone;
  }

  /**
   * Lock the draft after all rooms are complete (commissioner only).
   * After finalization, picks cannot be made or undone.
   */
  static async finalizeDraft({ leagueId, seasonId, isCommissioner = false }) {
    if (!isCommissioner) {
      throw new Error('Only the commissioner can finalize the draft.');
    }

    const seasonRef = doc(db, 'leagues', leagueId, 'seasons', seasonId);

    await runTransaction(db, async transaction => {
      const seasonSnap = await transaction.get(seasonRef);
      if (!seasonSnap.exists()) throw new Error('League season not found.');

      const seasonData = seasonSnap.data();
      if (seasonData.draftStatus === DRAFT_STATUS.FINALIZED) {
        throw new Error('Draft is already finalized.');
      }
      if (seasonData.draftStatus !== DRAFT_STATUS.COMPLETE) {
        throw new Error('All draft picks must be complete before finalizing.');
      }
      if (!areAllRoomsComplete(seasonData.draftRooms)) {
        throw new Error('All draft rooms must be complete before finalizing.');
      }

      transaction.update(seasonRef, {
        draftStatus: DRAFT_STATUS.FINALIZED,
        draftFinalizedAt: serverTimestamp(),
      });
    });

    await notifyDraftEmail({
      type: 'draftFinalized',
      leagueId,
      seasonId,
    });
  }

  static listenToTeams(leagueId, seasonId, callback) {
    const coll = collection(
      db,
      'leagues',
      leagueId,
      'seasons',
      seasonId,
      'teams'
    );
    return onSnapshot(coll, snapshot => {
      const teams = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(teams);
    });
  }
}

export default DraftService;
